import { createSlice } from '@reduxjs/toolkit';
import { ProTrialInfo } from '../../Models/Tiers/TierModels';
import { getCurrentPlan } from './TiersSlice';

// PR-3634 contracts v4 §6.2: Pro free trial state. No own thunk - it is read from
// FeatureTier/GetCurrentPlan (Data.ProTrial), so every existing getCurrentPlan dispatch refreshes it.
interface ProTrialState {
    info: ProTrialInfo | null;
    loaded: boolean;
}

const initialState: ProTrialState = {
    info: null,
    loaded: false,
};

const ProTrialSlice = createSlice({
    name: 'proTrial',
    initialState,
    reducers: {
        resetProTrialState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCurrentPlan.fulfilled, (state, action) => {
                const payload = action.payload;
                // ProTrial is null when there is no locked trial or the API could not read it (§5.2)
                state.info = payload?.StatusCode === 200 ? (payload.Data?.ProTrial ?? null) : null;
                state.loaded = true;
            })
            .addCase(getCurrentPlan.rejected, (state) => {
                state.info = null;
                state.loaded = true;
            });
    },
});

// Selectors (§6.2)
export const selectProTrialInfo = (state: any): ProTrialInfo | null => state.proTrial?.info ?? null;
export const selectProTrialLoaded = (state: any): boolean => state.proTrial?.loaded === true;
export const selectIsOnProTrial = (state: any): boolean => selectProTrialInfo(state)?.IsOnTrial === true;
export const selectIsProTrialLocked = (state: any): boolean => selectProTrialInfo(state)?.IsTrialLocked === true;
export const selectProTrialDaysRemaining = (state: any): number => selectProTrialInfo(state)?.DaysRemaining ?? 0;
export const selectProTrialEndDate = (state: any): string | null => selectProTrialInfo(state)?.EndDate ?? null;

// Days-left line (§6.3 / §6.4): the final day shows lastDay instead of "1 days remaining"
export const getProTrialDaysRemainingText = (
    t: (key: string, options?: any) => string,
    daysRemaining: number
): string =>
    daysRemaining === 1
        ? t('billing.tier.proTrial.lastDay')
        : t('billing.tier.proTrial.daysRemaining', { days: daysRemaining });

export const { resetProTrialState } = ProTrialSlice.actions;
export default ProTrialSlice.reducer;
